/**
 * THE VACANCY, on the definition the board reports.
 *
 * A vacant seat is not a vacancy. The 19-Aug pack review settled the step-down
 * and every board figure since has used it:
 *
 *     vacant employee seats
 *       less lecturer capacity provisions   held against faculty teaching plans,
 *                                           not named posts to recruit into
 *       = open positions
 *       less seats the 2026 budget does not fund
 *       = VACANCIES
 *
 * At 7 September that is 308 → 113 → 195 → 14 → 181. This dashboard used to show
 * "approved less filled" — 310 on the same day — under the word vacancy, which
 * overstates it by seventy per cent and disagrees with the papers the Committee
 * holds. The whole step-down is rendered, not just the answer, because the
 * question people actually ask is "why is your number different from mine".
 *
 * TWO STANDING RULES ARE ENFORCED IN THE LAYOUT, not left to the reader:
 *
 *  1. Capacity provisions are shown BESIDE the vacancies and never added to
 *     them. They are funded, but they are not posts to recruit into.
 *  2. Employees in post against an unbudgeted seat are NEVER presented in the
 *     same block as the vacancy count — the review was explicit, "even if we
 *     have to manually reconcile". They sit below a rule, under their own
 *     heading, framed as the budgeting question they are.
 *
 * LIVE FRAME ONLY. The budget is current state; there is no history of what it
 * said in August 2024. Reading today's money against an old establishment would
 * date the seats and the money differently, so on any historical date this panel
 * says so rather than showing a number computed across two dates.
 */
import React, { type ReactNode } from 'react';
import { PanelShell, PanelSkeleton } from './PanelShell';
import {
  Def,
  fmtNum,
  fmtPct,
  type DefLookup,
  type WorkforceVacancy,
} from './apiContract';

interface Props {
  vacancy: WorkforceVacancy | null | undefined;
  periodLabel: string;
  /** True when the selected date is the live frame — decides the "why not" copy. */
  isLive: boolean;
  loading: boolean;
  def: DefLookup;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Band order is the producer's; declared here so a missing band still renders
 *  as zero rather than dropping out of the strip and changing the shares. */
const AGE_BANDS = ['Under 3 months', '3-6 months', '6-12 months', 'Over 12 months'];

/** The Remco pack's own escalation ramp, so the screen and the deck agree on
 *  what "old" looks like. */
const AGE_COLORS: Record<string, string> = {
  'Under 3 months': '#2E6FD9',
  '3-6 months': '#5B6B85',
  '6-12 months': '#9C6209',
  'Over 12 months': '#B3322C',
};

/** One row of the step-down. `tone` carries the arithmetic, not just colour. */
const StepRow: React.FC<{
  label: ReactNode;
  perm: number | null;
  ftc: number | null;
  total: number | null;
  tone?: 'plain' | 'less' | 'result' | 'sub';
}> = ({ label, perm, ftc, total, tone = 'plain' }) => (
  <tr
    className={
      tone === 'result'
        ? 'border-y border-heritage-blue/20 bg-heritage-blue/[0.04] font-semibold text-heritage-blue'
        : tone === 'sub'
          ? 'text-gray-500'
          : 'text-gray-700'
    }
  >
    <td className={`py-1 pr-2 ${tone === 'less' || tone === 'sub' ? 'pl-4' : ''}`}>{label}</td>
    <td className="py-1 text-right tabular-nums">{fmtNum(perm)}</td>
    <td className="py-1 text-right tabular-nums">{fmtNum(ftc)}</td>
    <td className="py-1 pl-2 text-right font-semibold tabular-nums text-heritage-blue">
      {fmtNum(total)}
    </td>
  </tr>
);

export const VacancyStepDownPanel: React.FC<Props> = ({
  vacancy,
  periodLabel,
  isLive,
  loading,
  def,
}) => {
  const perm = vacancy?.by_contract?.Permanent;
  const ftc = vacancy?.by_contract?.FTC;
  const laterFrom = vacancy ? MONTHS[Math.max(0, Math.min(11, vacancy.later_from_month - 1))] : '';

  return (
    <PanelShell
      title="Vacancies"
      defKey="vacancies"
      def={def}
      window={
        vacancy
          ? `Permanent and fixed-term seats at ${periodLabel}, against the ${vacancy.budget_year} budget — the basis the board pack reports.`
          : `Permanent and fixed-term seats — computed against the current budget, so available on the live date only.`
      }
      right={
        vacancy ? (
          <div className="text-right">
            <div className="text-2xl font-bold leading-none tabular-nums text-heritage-blue">
              {fmtNum(vacancy.vacancies)}
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              vacancies
            </div>
          </div>
        ) : null
      }
      footnote={
        vacancy ? (
          <>
            A seat with <b>no {vacancy.budget_year} budget line at all</b> counts as funded — it was
            created after the budget was set, so it has never been declined. Only a line that exists
            and carries nothing in any month is an unfunded seat. “Activates later” means the first
            budgeted month is {laterFrom} or after, so the same seats move between the two rows as
            the year runs on — they were “September or later” in the August pack.
          </>
        ) : null
      }
    >
      {loading ? (
        <PanelSkeleton height={260} />
      ) : !vacancy ? (
        <div className="rounded border border-gray-200 bg-gray-50 p-3 text-[11.5px] leading-relaxed text-gray-600">
          <strong className="text-gray-700">Not computed for {periodLabel}.</strong>{' '}
          {isLive ? (
            <>
              The last rebuild did not produce a vacancy step-down. Either it ran before this measure
              existed, or it could not read the budget and left the step-down out rather than publish
              every seat as unfunded. The next rebuild adds it; the establishment figures above are
              unaffected either way.
            </>
          ) : (
            <>
              The vacancy test needs the position budget, and the budget is current state — there is
              no record of what it said on this date. Reading today’s money against this
              establishment would date the seats and the money differently, so the step-down is shown
              on the live date only.
            </>
          )}
        </div>
      ) : (
        <>
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="border-b border-gray-200 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-2 text-left font-semibold">Step</th>
                <th className="py-1 text-right font-semibold">Permanent</th>
                <th className="py-1 text-right font-semibold">Fixed-term</th>
                <th className="py-1 pl-2 text-right font-semibold">Employees</th>
              </tr>
            </thead>
            <tbody>
              <StepRow
                label="Approved seats"
                perm={perm?.seats ?? null}
                ftc={ftc?.seats ?? null}
                total={vacancy.seats}
              />
              <StepRow
                label="In post"
                perm={perm?.filled ?? null}
                ftc={ftc?.filled ?? null}
                total={vacancy.filled}
              />
              <StepRow
                label="Vacant seats"
                perm={perm?.vacant ?? null}
                ftc={ftc?.vacant ?? null}
                total={vacancy.vacant}
              />
              <StepRow
                tone="less"
                label={
                  <>
                    less lecturer capacity provisions
                    <Def def={def} k="capacity_provisions" />
                  </>
                }
                perm={perm?.capacity_provisions ?? null}
                ftc={ftc?.capacity_provisions ?? null}
                total={vacancy.capacity_provisions}
              />
              <StepRow
                label="Open positions"
                perm={perm?.open ?? null}
                ftc={ftc?.open ?? null}
                total={vacancy.open}
              />
              <StepRow
                tone="less"
                label={`less seats the ${vacancy.budget_year} budget does not fund`}
                perm={perm?.unfunded_vacant ?? null}
                ftc={ftc?.unfunded_vacant ?? null}
                total={vacancy.unfunded_vacant}
              />
              <StepRow
                tone="result"
                label="VACANCIES — the reported figure"
                perm={perm?.vacancies ?? null}
                ftc={ftc?.vacancies ?? null}
                total={vacancy.vacancies}
              />
              <StepRow
                tone="sub"
                label="live now"
                perm={perm?.now ?? null}
                ftc={ftc?.now ?? null}
                total={vacancy.now}
              />
              <StepRow
                tone="sub"
                label={`first budgeted month ${laterFrom} or later`}
                perm={perm?.later ?? null}
                ftc={ftc?.later ?? null}
                total={vacancy.later}
              />
              <StepRow
                label="Funded seats — the denominator"
                perm={perm?.funded_seats ?? null}
                ftc={ftc?.funded_seats ?? null}
                total={vacancy.funded_seats}
              />
              <tr className="border-t border-gray-200 font-semibold text-heritage-blue">
                <td className="py-1 pr-2">
                  Open rate on funded seats
                  <Def def={def} k="open_rate" />
                </td>
                <td className="py-1 text-right tabular-nums">{fmtPct(perm?.open_rate ?? null)}</td>
                <td className="py-1 text-right tabular-nums">{fmtPct(ftc?.open_rate ?? null)}</td>
                <td className="py-1 pl-2 text-right tabular-nums">{fmtPct(vacancy.open_rate)}</td>
              </tr>
            </tbody>
          </table>

          {/* ── how long the vacancies have been open ───────────────────────
              On the SAME 181 the step-down lands on. The page used to age a
              different population under the same word — "real vacancies" and
              "capacity seats", the vocabulary the 19-Aug review retired — which
              put two different vacancy totals on one screen. One basis, one
              number. Age runs from the seat's last assignment end, or from its
              approval where it has never been filled, so a seat approved for
              later in the year ages without ever having been a gap in a team. */}
          <div className="mt-3 border-t border-gray-100 pt-2.5">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              How long they have been open
            </div>
            <div className="flex h-2.5 w-full overflow-hidden rounded-sm">
              {AGE_BANDS.map((band) => {
                const n = vacancy.aging?.[band] ?? 0;
                if (!n || !vacancy.vacancies) return null;
                return (
                  <div
                    key={band}
                    title={`${band}: ${n}`}
                    style={{
                      width: `${(n / vacancy.vacancies) * 100}%`,
                      background: AGE_COLORS[band],
                    }}
                  />
                );
              })}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600">
              {AGE_BANDS.map((band) => (
                <span key={band} className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: AGE_COLORS[band] }}
                  />
                  {band}{' '}
                  <b className="tabular-nums text-gray-800">{fmtNum(vacancy.aging?.[band] ?? 0)}</b>
                </span>
              ))}
            </div>
          </div>

          {/* Benchmarks: the review asked that a vacancy rate never appear alone. */}
          <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-600">
            <span>
              Healthy vacancy rate{' '}
              <b className="tabular-nums text-gray-800">
                {fmtPct(vacancy.benchmarks.healthy_vacancy_rate)}
              </b>
            </span>
            <span>
              Salary-savings assumption in the budget{' '}
              <b className="tabular-nums text-gray-800">
                {fmtPct(vacancy.benchmarks.salary_savings_assumption)}
              </b>
            </span>
            <span>
              Capacity provisions the budget funds{' '}
              <b className="tabular-nums text-gray-800">{fmtNum(vacancy.provisions_funded)}</b>{' '}
              <span className="text-gray-500">— shown beside, never added</span>
            </span>
          </div>

          {/* ── the budgeting question, kept apart from the vacancy count ──── */}
          <div className="mt-3 rounded border border-amber-200 bg-amber-50/60 p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
              In post against an unbudgeted seat
              <Def def={def} k="incumbents_without_budget" />
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[11.5px] text-amber-900">
              <span className="text-xl font-bold leading-none tabular-nums">
                {fmtNum(vacancy.incumbents_without_budget)}
              </span>
              <span>
                {fmtNum(perm?.unfunded_incumbents ?? null)} permanent ·{' '}
                {fmtNum(ftc?.unfunded_incumbents ?? null)} fixed-term
                {vacancy.no_line_incumbents
                  ? ` · ${fmtNum(vacancy.no_line_incumbents)} with no budget line at all`
                  : ''}
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-amber-800">
              A budgeting item, not a vacancy — these people are in post and paid. Reported here
              rather than beside the vacancy count, and never netted off it.
            </p>
          </div>
        </>
      )}
    </PanelShell>
  );
};
